import { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Form,
  Input,
  Label,
  FormFeedback,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import toastr from "toastr";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { GOOGLE_MAP_KEY } from "../../environtments/environtment";

// Phone input component
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

// Google Maps
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useLoadScript,
} from "@react-google-maps/api";

// API hooks
import {
  useUpdateBooking,
  useGetClientBookingDetails,
} from "../../api/booking";
import { useGetAllCertsNotPaginated } from "../../api/certs";
import { useGetAllSectorsNotPaginated } from "../../api/sector";
import {
  parseSupervisorsListResponse,
  useSupervisorsListForBooking,
} from "../../api/supervisors";
import {
  emptySupervisor,
  flattenFormikErrorPaths,
  errorPathBelongsToStep,
  getStepTouched,
  isBookingShiftEditable,
  isDuplicateSupervisorInShift,
  mapSupervisorFromApi,
  mapSupervisorToPayload,
  maxShiftPatternsForBooking,
  experienceLevelOptions,
  mergeStepErrors,
  toastStepValidationErrors,
  validateFormikLocation,
  yupValidationErrorToFormik,
} from "./bookingFormShared";
import { buildBookingValidationSchema } from "./bookingSchemas";
// import { useAuthStore } from "../../service/useAuthStore"; // Removed onboarding check

/* ======================
   Helpers & Constants
====================== */
const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "380px", borderRadius: 12 };
const defaultCenter = { lat: 51.509865, lng: -0.118092 }; // London fallback

const toISODate = (d) => dayjs(d).format("YYYY-MM-DD");
const isOnOrAfter = (bISO, aISO) =>
  !bISO || !aISO
    ? true
    : dayjs(bISO).startOf("day").diff(dayjs(aISO).startOf("day"), "day") >= 0;

const parseHHMM = (hhmm) => {
  const [h = "0", m = "0"] = String(hhmm || "").split(":");
  return { h: Number(h), m: Number(m) };
};
const diffHours = (start, end) => {
  // both "HH:MM", allow overnight (end < start)
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  const sMin = s.h * 60 + s.m;
  let eMin = e.h * 60 + e.m;
  if (Number.isNaN(sMin) || Number.isNaN(eMin)) return 0;
  if (eMin <= sMin) eMin += 24 * 60;
  return (eMin - sMin) / 60;
};

const hhmmToMinutes = (hhmm) => {
  const { h, m } = parseHHMM(hhmm);
  return h * 60 + m;
};
const minutesToHHMM = (mins) => {
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, "0");
  const m = (mins % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};

// min start based on booking type
const reactSelectMenuPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({ ...base, zIndex: 99999, backgroundColor: "#fff" }),
};

const minStartISOForType = (bookingType) => {
  const now = dayjs();
  if (bookingType === "urgent") {
    // allow today if now+6h still today; else tomorrow
    const min = now.add(6, "hour");
    return toISODate(min);
  }
  // not urgent: tomorrow is allowed if start time is after current time
  return toISODate(now.add(1, "day"));
};

// Validate urgent booking constraints
const validateUrgentBooking = (
  startDate,
  startTime,
  isUrgent,
  endTime = null
) => {
  const now = dayjs();
  const selectedDate = dayjs(startDate);
  const selectedTime = dayjs(`${startDate} ${startTime}`);

  if (isUrgent) {
    // Urgent booking: must be 6-24 hours from now
    const minTime = now.add(6, "hour");
    const maxTime = now.add(24, "hour");

    if (selectedTime.isBefore(minTime)) {
      return `For urgent bookings, start time must be at least 6 hours from now (${minTime.format(
        "DD MMM YYYY, h:mm A"
      )})`;
    }

    if (selectedTime.isAfter(maxTime)) {
      return `For urgent bookings, start time must be within 24 hours from now (${maxTime.format(
        "DD MMM YYYY, h:mm A"
      )})`;
    }

    // If selecting today's date, start time must be after current time
    if (selectedDate.isSame(now, "day")) {
      const currentTime = now.format("HH:mm");
      if (startTime <= currentTime) {
        return `For urgent bookings on the same day, start time must be after current time (${currentTime})`;
      }
    }

    // Validate end time: entire shift must complete within 24 hours from now
    if (endTime) {
      let endDateTime;
      if (endTime < startTime) {
        // Overnight shift - end time is next day
        endDateTime = dayjs(`${startDate} ${endTime}`).add(1, "day");
      } else {
        // Same day shift
        endDateTime = dayjs(`${startDate} ${endTime}`);
      }

      if (endDateTime.isAfter(maxTime)) {
        return `For urgent bookings, the entire shift must complete within 24 hours from now. End time cannot be after ${maxTime.format(
          "DD MMM YYYY, h:mm A"
        )}`;
      }
    }
  } else {
    // Non-urgent booking: must be more than 24 hours from now
    const minTime = now.add(24, "hour");

    if (selectedTime.isBefore(minTime) || selectedTime.isSame(minTime)) {
      return `For non-urgent bookings, start time must be more than 24 hours from now (${minTime.format(
        "DD MMM YYYY, h:mm A"
      )})`;
    }

    // If selecting tomorrow's date, start time must be after current time
    const tomorrow = now.add(1, "day");
    if (selectedDate.isSame(tomorrow, "day")) {
      const currentTime = now.format("HH:mm");
      if (startTime <= currentTime) {
        return `For non-urgent bookings on tomorrow, start time must be after current time (${currentTime})`;
      }
    }
  }

  return null; // Valid
};

// sectors → roles (keep sectorSlug for payload)
const flattenRoles = (sectors) => {
  const items = [];
  (sectors || []).forEach((s) => {
    (s.roles || []).forEach((r) => {
      items.push({
        value: r.slug,
        label: `${r.title} — ${s.title}`,
        group: s.title,
        sectorSlug: s.slug,
      });
    });
  });
  return items;
};

// break-time rule helper (returns minimal minutes required given shift hours)
const minBreakMinutesFor = (shiftHours) => {
  if (shiftHours <= 6) return 0; // can be 0
  if (shiftHours > 9) return 30;
  return 20;
};

// Generate dates array from start/end date with weekend exclusion
const generateDatesArray = (
  startDate,
  endDate,
  excludeWeekend = false,
  excludeSpecificDates = ""
) => {
  if (!startDate || !endDate) return [];

  const dates = [];
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const excludeDates = excludeSpecificDates
    ? excludeSpecificDates.split(",").map((d) => d.trim())
    : [];

  let current = start;
  while (current.isSame(end, "day") || current.isBefore(end, "day")) {
    const dateStr = current.format("YYYY-MM-DD");

    // Skip if it's a weekend and excludeWeekend is true
    if (excludeWeekend && (current.day() === 0 || current.day() === 6)) {
      current = current.add(1, "day");
      continue;
    }

    // Skip if date is in excludeSpecificDates
    if (excludeDates.includes(dateStr)) {
      current = current.add(1, "day");
      continue;
    }

    dates.push(dateStr);
    current = current.add(1, "day");
  }

  return dates;
};

// Custom Date Picker Component for Exclude Dates
const ExcludeDatesPicker = ({
  startDate,
  endDate,
  excludeWeekend,
  selectedDates,
  onDatesChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!startDate || !endDate) {
    return (
      <div className="text-muted">Please select start and end dates first</div>
    );
  }

  const generateDateRange = () => {
    const dates = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    let current = start;
    while (current.isSame(end, "day") || current.isBefore(end, "day")) {
      const dateStr = current.format("YYYY-MM-DD");

      // Skip weekends if excludeWeekend is true
      if (excludeWeekend && (current.day() === 0 || current.day() === 6)) {
        current = current.add(1, "day");
        continue;
      }

      dates.push({
        date: dateStr,
        dayjs: current,
        isWeekend: current.day() === 0 || current.day() === 6,
      });
      current = current.add(1, "day");
    }

    return dates;
  };

  const availableDates = generateDateRange();
  const selectedDatesArray = selectedDates
    ? selectedDates.split(",").map((d) => d.trim())
    : [];

  const toggleDate = (dateStr) => {
    const newSelectedDates = selectedDatesArray.includes(dateStr)
      ? selectedDatesArray.filter((d) => d !== dateStr)
      : [...selectedDatesArray, dateStr];

    onDatesChange(newSelectedDates.join(","));
  };

  return (
    <div className="exclude-dates-picker">
      <Button
        type="button"
        color="outline-secondary"
        onClick={() => setIsOpen(!isOpen)}
        className="w-100"
      >
        {selectedDatesArray.length > 0
          ? `Exclude ${selectedDatesArray.length} date${
              selectedDatesArray.length > 1 ? "s" : ""
            }`
          : "Select dates to exclude"}
      </Button>

      {isOpen && (
        <div
          className="date-picker-modal mt-2 p-3 border rounded bg-white shadow"
          style={{ zIndex: 1000, position: "relative" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Select dates to exclude</h6>
            <Button
              type="button"
              size="sm"
              color="outline-secondary"
              onClick={() => setIsOpen(false)}
            >
              ×
            </Button>
          </div>

          <div
            className="date-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
              maxWidth: "300px",
            }}
          >
            {availableDates.map((dateObj) => {
              const isSelected = selectedDatesArray.includes(dateObj.date);
              const isToday = dateObj.dayjs.isSame(dayjs(), "day");

              return (
                <button
                  key={dateObj.date}
                  type="button"
                  className={`btn btn-sm ${
                    isSelected
                      ? "btn-danger"
                      : isToday
                      ? "btn-warning"
                      : dateObj.isWeekend
                      ? "btn-outline-secondary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => toggleDate(dateObj.date)}
                  style={{
                    fontSize: "0.8rem",
                    padding: "6px 4px",
                    minHeight: "32px",
                  }}
                  title={`${dateObj.dayjs.format("DD MMM YYYY")}${
                    isSelected ? " (Excluded)" : ""
                  }`}
                >
                  {dateObj.dayjs.format("DD")}
                </button>
              );
            })}
          </div>

          <div className="mt-3">
            <small className="text-muted">
              <span className="badge bg-primary me-1">Available</span>
              <span className="badge bg-warning me-1">Today</span>
              <span className="badge bg-danger me-1">Excluded</span>
              <span className="badge bg-secondary">Weekend</span>
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

ExcludeDatesPicker.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  excludeWeekend: PropTypes.bool,
  selectedDates: PropTypes.string,
  onDatesChange: PropTypes.func.isRequired,
};

// Default initial values (constant)
const defaultInitialValues = {
  isUrgent: false,
  bookingType: "not_urgent",
  hasPORequired: false,
  poReference: "",
  poDocument: null,
  shiftPattern: [
    {
      sectorSlug: "",
      roleSlug: "",
      quantity: 1,
      hasPPEProvided: "not_required",
      ppeInstruction: "",
      startDate: "",
      endDate: "",
      excludeWeekend: false,
      excludeSpecificDates: "",
      dates: [],
      startTime: "09:00",
      endTime: "17:00",
      breakTime: "00:30",
      supervisor: [emptySupervisor()],
      shiftDetails: "",
      experienceLevel: [],
      certSlugs: [],
    },
  ],
  timezone: "Europe/London",
  location: {
    siteName: "",
    searchAddress: "",
    fullAddress: "",
    accessNotes: "",
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
  },
  notes: "",
};

/* ======================
   Component
====================== */
const BookingUpdate = () => {
  document.title = "Update Booking | JobArk";
  const navigate = useNavigate();
  const { bookingSlug } = useParams();
  // const hasCompletedOnBoarding = useAuthStore((s) => s.hasCompletedOnBoarding); // Removed onboarding check

  // Load existing booking data
  const { data: bookingData, isLoading: isLoadingBooking } =
    useGetClientBookingDetails(bookingSlug);
  const booking = useMemo(
    () => bookingData?.data?.data ?? bookingData?.data ?? bookingData,
    [bookingData]
  );

  const canEditShifts = useMemo(
    () => isBookingShiftEditable(booking?.status),
    [booking?.status]
  );
  const canEditLocationAndTimezone = false;
  const maxStep = canEditShifts ? 3 : 2;
  const hasExistingPoDocument = !!(
    booking?.document ||
    booking?.poDocument ||
    booking?.poDocumentPath
  );

  // Google Maps
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  // Sectors/Roles source
  const { data: sectorsRes, isLoading: sectorsLoading } =
    useGetAllSectorsNotPaginated();

  // useEffect(() => {
  //   loadSectors();
  // }, [loadSectors]);

  const roleOptions = useMemo(
    () => flattenRoles(sectorsRes?.data || []),
    [sectorsRes]
  );

  const { mutate: loadComplianceCerts, data: complianceCertsRes } =
    useGetAllCertsNotPaginated();
  const complianceCertOptions = useMemo(() => {
    const list =
      complianceCertsRes?.data?.data || complianceCertsRes?.data || [];
    return (Array.isArray(list) ? list : [])
      .filter((cert) => cert?.isActive !== false && cert?.slug)
      .map((cert) => ({
        value: cert.slug,
        label: cert.title || cert.slug,
      }));
  }, [complianceCertsRes]);

  useEffect(() => {
    loadComplianceCerts({ isComplianceCertificate: true });
  }, [loadComplianceCerts]);

  // Success modal
  const [showSuccess, setShowSuccess] = useState(false);
  const handleCloseModal = () => {
    setShowSuccess(false);
    navigate("/client/bookings");
  };

  // Stepper
  const [step, setStep] = useState(1);
  const isReviewStep = canEditShifts ? step === 3 : step === 2;

  const { data: supervisorsRes, isLoading: supervisorsLoading } =
    useSupervisorsListForBooking();

  const supervisorOptions = useMemo(() => {
    return parseSupervisorsListResponse(supervisorsRes).map((sup) => ({
      value: sup.slug,
      label: `${sup.fullName || sup.slug} — ${sup.email || ""}`,
      supervisor: sup,
    }));
  }, [supervisorsRes]);

  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  // Update
  const { mutate: updateBookingMutation, isPending } = useUpdateBooking();

  // Helper function to map booking data to form values
  const mapBookingToFormValues = useCallback((bookingData, sectorsData) => {
    if (!bookingData) return null;

    // Helper to find sector slug from sectorId
    const findSectorSlug = (sectorId) => {
      if (!sectorId || !sectorsData) return "";
      const sector = sectorsData.find((s) => s.id === sectorId);
      return sector?.slug || "";
    };

    // Map shift patterns
    const shiftPattern = (bookingData.shiftPattern || []).map((pattern) => {
      // Handle dates array - can be array of strings or array of objects with {date, remaining}
      const datesArray = pattern.dates || [];
      const extractedDates = datesArray
        .map((d) => (typeof d === "string" ? d : d.date || d))
        .filter(Boolean);

      // Extract startDate and endDate from dates array
      const sortedDates = [...extractedDates].sort();
      const startDate = sortedDates.length > 0 ? sortedDates[0] : "";
      const endDate =
        sortedDates.length > 0 ? sortedDates[sortedDates.length - 1] : "";

      const sectorSlug =
        pattern.sectorSlug ||
        pattern.sector?.slug ||
        findSectorSlug(pattern.sectorId) ||
        "";

      const roleSlug = pattern.roleSlug || pattern.role?.slug || "";

      const supervisorRows = pattern.supervisors || pattern.supervisor || [];

      return {
        sectorSlug,
        roleSlug,
        quantity: pattern.quantity || 1,
        hasPPEProvided:
          pattern.hasPPEProvided === true || pattern.hasPPEProvided === "yes"
            ? "yes"
            : "not_required",
        ppeInstruction: pattern.ppeInstruction || "",
        startDate,
        endDate,
        excludeWeekend: false,
        excludeSpecificDates: "",
        dates: extractedDates.length > 0 ? extractedDates : [],
        startTime: pattern.startTime || "09:00",
        endTime: pattern.endTime || "17:00",
        breakTime: pattern.breakTime || "00:30",
        supervisor:
          supervisorRows.length > 0
            ? supervisorRows.map((s) => mapSupervisorFromApi(s))
            : [emptySupervisor()],
        shiftDetails: pattern.shiftDetails || "",
        experienceLevel: Array.isArray(pattern.experienceLevel)
          ? pattern.experienceLevel.filter(Boolean)
          : [],
        certSlugs: Array.isArray(pattern.certSlugs)
          ? pattern.certSlugs.filter(Boolean)
          : [],
      };
    });

    return {
      isUrgent: bookingData.bookingType === "urgent",
      bookingType: bookingData.bookingType || "not_urgent",
      hasPORequired: bookingData.hasPORequired || false,
      poReference: bookingData.poReference || "",
      poDocument: null, // File cannot be loaded from API
      shiftPattern:
        shiftPattern.length > 0
          ? shiftPattern
          : [
              {
                sectorSlug: "",
                roleSlug: "",
                quantity: 1,
                hasPPEProvided: "not_required",
                ppeInstruction: "",
                startDate: "",
                endDate: "",
                excludeWeekend: false,
                excludeSpecificDates: "",
                dates: [],
                startTime: "09:00",
                endTime: "17:00",
                breakTime: "00:30",
                supervisor: [emptySupervisor()],
                shiftDetails: "",
                experienceLevel: [],
                certSlugs: [],
              },
            ],
      timezone: bookingData.timezone || "Europe/London",
      location: {
        siteName: bookingData.location?.siteName || "",
        searchAddress: bookingData.location?.searchAddress || "",
        fullAddress: bookingData.location?.fullAddress || "",
        accessNotes: bookingData.location?.accessNotes || "",
        latitude: bookingData.location?.latitude || defaultCenter.lat,
        longitude: bookingData.location?.longitude || defaultCenter.lng,
      },
      notes: bookingData.notes || "",
    };
  }, []);

  const validationSchema = useMemo(
    () =>
      buildBookingValidationSchema(
        Yup,
        {
          validateUrgentBooking,
          diffHours,
          hhmmToMinutes,
          minBreakMinutesFor,
          dayjs,
        },
        {
          hasExistingPoDocument,
          includeShiftFields: canEditShifts,
          includeAgreements: false,
          includeLocationAndTimezone: canEditLocationAndTimezone,
        }
      ),
    [canEditShifts, hasExistingPoDocument, canEditLocationAndTimezone]
  );

  // Get mapped initial values from booking data
  const initialValues = useMemo(() => {
    if (booking && sectorsRes?.data) {
      const mapped = mapBookingToFormValues(booking, sectorsRes.data);
      if (mapped) return mapped;
    }
    return defaultInitialValues;
  }, [booking, sectorsRes, mapBookingToFormValues]);

  // ====== Formik ======
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (canEditShifts) {
        const minISO = minStartISOForType(
          values.isUrgent ? "urgent" : "not_urgent"
        );
        const earliest = values.shiftPattern.flatMap((p) => p.dates).sort()[0];
        if (earliest && !isOnOrAfter(earliest, minISO)) {
          toastr.error(
            `Earliest allowed date is ${dayjs(minISO).format(
              "ddd, DD MMM YYYY"
            )} for this booking type.`
          );
          return;
        }
      }

      const withPlus = (d) => (d ? (d.startsWith("+") ? d : `+${d}`) : d);

      const fd = new FormData();
      fd.append("hasPORequired", String(values.hasPORequired));
      if (values.hasPORequired) {
        fd.append("poReference", values.poReference);
        if (values.poDocument) {
          fd.append("document", values.poDocument);
        }
      }
      if (canEditLocationAndTimezone) {
        fd.append("timezone", values.timezone);
        fd.append(
          "location",
          JSON.stringify({
            siteName: values.location.siteName,
            searchAddress: values.location.searchAddress,
            fullAddress: values.location.fullAddress,
            accessNotes: values.location.accessNotes,
            latitude: Number(values.location.latitude),
            longitude: Number(values.location.longitude),
          })
        );
      }
      if (values.notes) fd.append("notes", values.notes);

      if (canEditShifts) {
        fd.append("bookingType", values.isUrgent ? "urgent" : "not_urgent");
        const shiftPattern = values.shiftPattern.map((p) => ({
          sectorSlug: p.sectorSlug,
          roleSlug: p.roleSlug,
          quantity: Number(p.quantity),
          hasPPEProvided: p.hasPPEProvided === "yes",
          ...(p.hasPPEProvided === "yes"
            ? { ppeInstruction: p.ppeInstruction }
            : {}),
          dates: generateDatesArray(
            p.startDate,
            p.endDate,
            p.excludeWeekend,
            p.excludeSpecificDates
          ),
          startTime: p.startTime,
          endTime: p.endTime,
          breakTime: p.breakTime,
          supervisor: p.supervisor.map((s) =>
            mapSupervisorToPayload(s, withPlus)
          ),
          shiftDetails: p.shiftDetails,
          experienceLevel: p.experienceLevel || [],
          ...(p.certSlugs?.length ? { certSlugs: p.certSlugs } : {}),
        }));
        fd.append("shiftPattern", JSON.stringify(shiftPattern));
      }

      updateBookingMutation(
        { bookingSlug, payload: fd },
        {
          onSuccess: () => {
            toastr.success("Booking updated successfully");
            setShowSuccess(true);
          },
          onError: () => {},
          isMultipart: true,
        }
      );
    },
  });

  // Ensure dates array is populated when form initializes with booking data
  useEffect(() => {
    if (
      booking &&
      formik.values.shiftPattern &&
      formik.values.shiftPattern.length > 0
    ) {
      const needsUpdate = formik.values.shiftPattern.some((pattern) => {
        return (
          pattern.startDate &&
          pattern.endDate &&
          (!pattern.dates || pattern.dates.length === 0)
        );
      });

      if (needsUpdate) {
        const updatedPatterns = formik.values.shiftPattern.map((pattern) => {
          if (
            pattern.startDate &&
            pattern.endDate &&
            (!pattern.dates || pattern.dates.length === 0)
          ) {
            const generatedDates = generateDatesArray(
              pattern.startDate,
              pattern.endDate,
              pattern.excludeWeekend || false,
              pattern.excludeSpecificDates || ""
            );
            return { ...pattern, dates: generatedDates };
          }
          return pattern;
        });
        formik.setFieldValue("shiftPattern", updatedPatterns, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking, formik.values.shiftPattern?.length]);

  const validatePatternsBusinessRules = useCallback((values) => {
    const maxPatterns = maxShiftPatternsForBooking(values.isUrgent);
    if ((values.shiftPattern || []).length > maxPatterns) {
      return values.isUrgent
        ? "Urgent bookings can only have one shift pattern."
        : "Maximum 3 shift patterns allowed.";
    }
    for (const [i, p] of (values.shiftPattern || []).entries()) {
      const dur = diffHours(p.startTime, p.endTime);
      if (dur < 4 || dur > 12) {
        return `Shift Pattern ${
          i + 1
        }: shift duration must be between 4 and 12 hours.`;
      }
      const minBreak = minBreakMinutesFor(dur);
      if (hhmmToMinutes(p.breakTime) < minBreak) {
        return `Shift Pattern ${
          i + 1
        }: break must be at least ${minBreak} minutes.`;
      }
      const generatedDates = generateDatesArray(
        p.startDate,
        p.endDate,
        p.excludeWeekend,
        p.excludeSpecificDates
      );
      if (generatedDates.length === 0 && p.startDate && p.endDate) {
        return `Shift Pattern ${
          i + 1
        }: no valid dates found in the specified range.`;
      }
      if (p.startDate && p.startTime) {
        const urgentError = validateUrgentBooking(
          p.startDate,
          p.startTime,
          values.isUrgent,
          p.endTime
        );
        if (urgentError) {
          return `Shift Pattern ${i + 1}: ${urgentError}`;
        }
      }
    }
    const minISO = minStartISOForType(
      values.isUrgent ? "urgent" : "not_urgent"
    );
    const allDates = values.shiftPattern
      .flatMap((p) =>
        generateDatesArray(
          p.startDate,
          p.endDate,
          p.excludeWeekend,
          p.excludeSpecificDates
        )
      )
      .sort();
    const earliest = allDates[0];
    if (earliest && !isOnOrAfter(earliest, minISO)) {
      if (values.isUrgent === false && earliest === minISO) {
        const tomorrowPattern = values.shiftPattern.find(
          (p) => p.startDate === earliest && p.startTime
        );
        if (tomorrowPattern) {
          const urgentError = validateUrgentBooking(
            earliest,
            tomorrowPattern.startTime,
            false,
            tomorrowPattern.endTime
          );
          if (urgentError) return urgentError;
        }
      } else {
        return `Earliest allowed date is ${dayjs(minISO).format(
          "ddd, DD MMM YYYY"
        )} for this booking type.`;
      }
    }
    return null;
  }, []);

  const getStepSchema = useCallback(
    (currentStep) => {
      switch (currentStep) {
        case 1:
          return Yup.object({
            hasPORequired: validationSchema.fields.hasPORequired,
            poReference: validationSchema.fields.poReference,
            poDocument: validationSchema.fields.poDocument,
            ...(canEditLocationAndTimezone
              ? {
                  timezone: validationSchema.fields.timezone,
                  location: validationSchema.fields.location,
                }
              : {}),
            notes: validationSchema.fields.notes,
          });
        case 2:
          if (!canEditShifts) return null;
          return Yup.object({
            isUrgent: validationSchema.fields.isUrgent,
            shiftPattern: validationSchema.fields.shiftPattern,
          });
        default:
          return null;
      }
    },
    [validationSchema, canEditShifts, canEditLocationAndTimezone]
  );

  const nextStep = useCallback(async () => {
    const schema = getStepSchema(step);
    if (!schema) return;

    let stepErrors = {};
    try {
      await schema.validate(formik.values, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        stepErrors = yupValidationErrorToFormik(err);
      } else {
        toastr.error("Please fix validation errors to continue.");
        return;
      }
    }

    const stepErrorPaths = flattenFormikErrorPaths(stepErrors).filter((p) =>
      errorPathBelongsToStep(p, step)
    );

    if (step === 2) {
      const businessMsg = validatePatternsBusinessRules(formik.values);
      if (businessMsg) {
        formik.setTouched(
          { ...formik.touched, ...getStepTouched(step, formik.values) },
          false
        );
        toastr.error(`Validation Error: ${businessMsg}`);
        return;
      }
    }

    if (stepErrorPaths.length > 0) {
      formik.setErrors(mergeStepErrors(formik.errors, step, stepErrors));
      formik.setTouched(
        { ...formik.touched, ...getStepTouched(step, formik.values) },
        false
      );
      toastStepValidationErrors(step, stepErrors);
      return;
    }

    formik.setErrors(mergeStepErrors(formik.errors, step, {}));
    setStep((s) => Math.min(maxStep, s + 1));
  }, [step, formik, getStepSchema, validatePatternsBusinessRules, maxStep]);

  const setField = useCallback(
    (path, v, shouldValidate = true) =>
      formik.setFieldValue(path, v, shouldValidate),
    [formik]
  );

  const locationSchema = validationSchema.fields.location;

  const patchLocation = useCallback(
    async (patch, { validate = true } = {}) => {
      const nextLocation = { ...formik.values.location, ...patch };
      await formik.setFieldValue("location", nextLocation, false);
      formik.setFieldTouched(
        "location",
        {
          ...(formik.touched.location || {}),
          ...Object.fromEntries(Object.keys(patch).map((k) => [k, true])),
        },
        false
      );
      if (validate) {
        await validateFormikLocation(formik, locationSchema, nextLocation);
      }
    },
    [formik, locationSchema]
  );

  const locationAddressError =
    formik.errors.location?.fullAddress ||
    (typeof formik.errors.location === "string"
      ? formik.errors.location
      : null);
  const showLocationAddressError =
    !!locationAddressError &&
    (formik.touched.location?.searchAddress ||
      formik.touched.location?.fullAddress);

  // ====== Map bits ======
  const [auto, setAuto] = useState(null);
  const onLoadAutocomplete = (ac) => setAuto(ac);
  const applyPlaceToLocation = useCallback(
    (place) => {
      if (!place?.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || place.name || "";
      if (!address.trim()) return;
      void patchLocation({
        searchAddress: address,
        fullAddress: address,
        latitude: lat,
        longitude: lng,
      });
    },
    [patchLocation]
  );

  const onPlaceChanged = () => {
    window.setTimeout(() => {
      if (!auto) return;
      applyPlaceToLocation(auto.getPlace());
    }, 0);
  };

  const center = useMemo(
    () => ({
      lat: Number(formik.values.location.latitude || defaultCenter.lat),
      lng: Number(formik.values.location.longitude || defaultCenter.lng),
    }),
    [formik.values.location.latitude, formik.values.location.longitude]
  );

  const onMarkerDragEnd = useCallback(
    (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setField("location.latitude", lat);
      setField("location.longitude", lng);
    },
    [setField]
  );

  const useCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      toastr.error("Geolocation not supported by this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setField("location.latitude", latitude);
        setField("location.longitude", longitude);
        toastr.success("Location captured.");
      },
      (err) => {
        toastr.error(err?.message || "Unable to get location.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ====== Pattern mutators ======
  const addPattern = () => {
    const list = [...formik.values.shiftPattern];
    const maxPatterns = maxShiftPatternsForBooking(formik.values.isUrgent);
    if (list.length >= maxPatterns) {
      toastr.info(
        formik.values.isUrgent
          ? "Urgent bookings can only have one shift pattern."
          : "You can add up to 3 shift patterns."
      );
      return;
    }
    list.push({
      sectorSlug: "",
      roleSlug: "",
      quantity: 1,
      hasPPEProvided: "not_required",
      ppeInstruction: "",
      startDate: "",
      endDate: "",
      excludeWeekend: false,
      excludeSpecificDates: "",
      dates: [],
      startTime: "09:00",
      endTime: "17:00",
      breakTime: "00:30",
      supervisor: [emptySupervisor()],
      shiftDetails: "",
      experienceLevel: [],
      certSlugs: [],
    });
    setField("shiftPattern", list);
  };

  const removePattern = (idx) => {
    const list = [...formik.values.shiftPattern];
    list.splice(idx, 1);
    setField("shiftPattern", list);
  };

  // Helper function to update dates array when date fields change
  const updateDatesArray = (patternIdx, patternData) => {
    const dates = generateDatesArray(
      patternData.startDate,
      patternData.endDate,
      patternData.excludeWeekend,
      patternData.excludeSpecificDates
    );
    return dates;
  };

  const addSupervisor = (pIdx) => {
    const list = [...formik.values.shiftPattern];
    list[pIdx].supervisor.push(emptySupervisor());
    setField("shiftPattern", list);
  };

  const setSupervisorSource = (pIdx, sIdx, source) => {
    const list = [...formik.values.shiftPattern];
    list[pIdx].supervisor[sIdx] =
      source === "existing"
        ? {
            source: "existing",
            supervisorSlug: "",
            name: "",
            mobile: "",
            email: "",
          }
        : emptySupervisor();
    setField("shiftPattern", list);
  };

  const selectExistingSupervisor = (pIdx, sIdx, option) => {
    const list = [...formik.values.shiftPattern];
    if (!option) {
      list[pIdx].supervisor[sIdx] = {
        source: "existing",
        supervisorSlug: "",
        name: "",
        mobile: "",
        email: "",
      };
    } else {
      const alreadyUsed = list[pIdx].supervisor.some(
        (row, i) =>
          i !== sIdx &&
          row.source === "existing" &&
          row.supervisorSlug === option.value
      );
      if (alreadyUsed) {
        toastr.error("This supervisor is already added to this shift");
        return;
      }
      const sup = option.supervisor;
      list[pIdx].supervisor[sIdx] = {
        source: "existing",
        supervisorSlug: option.value,
        name: sup.fullName || "",
        mobile: (sup.phoneNumber || "").replace(/^\+/, ""),
        email: sup.email || "",
      };
    }
    setField("shiftPattern", list);
    setTimeout(() => {
      formik.validateField(`shiftPattern.${pIdx}.supervisor`);
    }, 0);
  };

  const supervisorOptionsForShift = useCallback(
    (patternIdx, supervisorIdx) => {
      const rows = formik.values.shiftPattern?.[patternIdx]?.supervisor || [];
      const usedSlugs = new Set(
        rows
          .filter(
            (row, i) =>
              i !== supervisorIdx &&
              row.source === "existing" &&
              row.supervisorSlug
          )
          .map((row) => row.supervisorSlug)
      );
      const currentSlug = rows[supervisorIdx]?.supervisorSlug;
      return supervisorOptions.filter(
        (opt) => !usedSlugs.has(opt.value) || opt.value === currentSlug
      );
    },
    [formik.values.shiftPattern, supervisorOptions]
  );

  const removeSupervisor = (pIdx, sIdx) => {
    const list = [...formik.values.shiftPattern];
    list[pIdx].supervisor.splice(sIdx, 1);
    setField("shiftPattern", list);
  };

  // Step banners - REMOVED
  // const ruleText = formik.values.isUrgent
  //   ? "Urgent: earliest start is today if at least 6 hours from now; otherwise tomorrow."
  //   : "Not urgent: earliest start is the day after tomorrow (≈48 hours from now).";

  /* ======================
     Render
  ====================== */
  if (isLoadingBooking) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading booking data...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="text-muted">
              <i
                className="mdi mdi-information-outline"
                style={{ fontSize: "48px" }}
              ></i>
              <p className="mt-3">Booking not found.</p>
            </div>
            <Button
              color="primary"
              onClick={() => navigate("/client/bookings")}
            >
              Back to Bookings
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          {/* ========== STEP 1: Custom Card Design ========== */}
          {step === 1 && (
            <div className="custom-booking-card">
              {/* Custom Stepper */}
              <div className="custom-stepper">
                <div className="stepper-progress">
                  <div className={`stepper-progress-bar step-${step}`}></div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="mb-3 fw-bold text-dark">Update booking</h2>
                <p className="text-muted mb-0">
                  Update purchase order and notes for this booking. Location and
                  timezone are fixed after creation.
                  {canEditShifts
                    ? " Shift patterns can be edited while the booking is pending."
                    : " Shift details cannot be changed after the booking leaves pending status."}
                </p>
              </div>

              {!canEditShifts && (
                <div className="alert alert-info mb-4">
                  Shifts are locked for this booking (status:{" "}
                  <strong>{booking?.status || "—"}</strong>). You can update PO
                  and notes only.
                </div>
              )}

              {/* Purchase Order Section */}
              <div className="section-gray">
                <h5 className="section-header">
                  Does this booking require a Purchase Order?
                </h5>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <Input
                          type="radio"
                          name="poRequired"
                          id="po-no"
                          checked={formik.values.hasPORequired === false}
                          onChange={() => {
                            setField("hasPORequired", false);
                            // Clear PO fields when not required
                            setField("poReference", "");
                            setField("poDocument", null);
                          }}
                          className="form-check-input"
                        />
                        <Label for="po-no" className="form-check-label">
                          No
                        </Label>
                      </div>
                      <div className="form-check">
                        <Input
                          type="radio"
                          name="poRequired"
                          id="po-yes"
                          checked={formik.values.hasPORequired === true}
                          onChange={() => setField("hasPORequired", true)}
                          className="form-check-input"
                        />
                        <Label for="po-yes" className="form-check-label">
                          Yes
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>

                {formik.values.hasPORequired && (
                  <Row className="g-3 mt-3">
                    <Col md={6}>
                      <Label>Purchase Order Reference</Label>
                      <Input
                        name="poReference"
                        value={formik.values.poReference}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.poReference &&
                          !!formik.errors.poReference
                        }
                        placeholder="PO-154151681"
                      />
                      <FormFeedback>{formik.errors.poReference}</FormFeedback>
                    </Col>
                    <Col md={6}>
                      <Label>Official PO document</Label>
                      {hasExistingPoDocument && (
                        <small className="text-muted d-block mb-1">
                          A document is already on file. Upload only if you need
                          to replace it.
                        </small>
                      )}
                      <Input
                        type="file"
                        onChange={(e) =>
                          setField(
                            "poDocument",
                            e.currentTarget.files?.[0] || null
                          )
                        }
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.poDocument &&
                          !!formik.errors.poDocument
                        }
                      />
                      {formik.touched.poDocument &&
                        formik.errors.poDocument && (
                          <div className="invalid-feedback d-block">
                            {formik.errors.poDocument}
                          </div>
                        )}
                    </Col>
                  </Row>
                )}
              </div>

              {/* Location & timezone (read-only on update) */}
              <div className="section-white">
                <h5 className="section-header">Location &amp; timezone</h5>
                {!canEditLocationAndTimezone && (
                  <p className="text-muted small mb-3">
                    Location and timezone cannot be changed after a booking is
                    created.
                  </p>
                )}
                <Row className="g-3">
                  {canEditLocationAndTimezone ? (
                    <>
                      <Col md={6}>
                    <Label>Site Name</Label>
                    <Input
                      name="location.siteName"
                      value={formik.values.location.siteName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.location?.siteName &&
                        !!formik.errors.location?.siteName
                      }
                      placeholder="John Doe"
                    />
                    <FormFeedback>
                      {formik.errors.location?.siteName}
                    </FormFeedback>
                  </Col>

                    <Col md={6}>
                      <Label>Search Address</Label>
                      {isLoaded ? (
                        <Autocomplete
                          onLoad={onLoadAutocomplete}
                          onPlaceChanged={onPlaceChanged}
                          options={{
                            fields: ["formatted_address", "geometry", "name"],
                          }}
                        >
                          <Input
                            placeholder="Type and pick an address"
                            value={formik.values.location.searchAddress}
                            onChange={(e) => {
                              const v = e.target.value;
                              const patch = { searchAddress: v };
                              if (
                                !v.trim() ||
                                v.trim() !==
                                  formik.values.location.fullAddress?.trim()
                              ) {
                                patch.fullAddress = "";
                              }
                              void patchLocation(patch, { validate: false });
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                "location.searchAddress",
                                true,
                                false
                              );
                              formik.setFieldTouched(
                                "location.fullAddress",
                                true,
                                false
                              );
                              const loc = formik.values.location;
                              if (!loc.fullAddress?.trim()) {
                                void validateFormikLocation(
                                  formik,
                                  locationSchema,
                                  loc
                                );
                              }
                            }}
                            name="location.searchAddress"
                            invalid={showLocationAddressError}
                          />
                        </Autocomplete>
                      ) : (
                        <Input disabled placeholder="Loading map..." />
                      )}
                      {showLocationAddressError && (
                        <div className="invalid-feedback d-block">
                          {locationAddressError}
                        </div>
                      )}
                    </Col>

                    <Col md={12}>
                      <Label>Full Address</Label>
                      <Input
                        name="location.fullAddress"
                        value={formik.values.location.fullAddress}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.location?.fullAddress &&
                          !!formik.errors.location?.fullAddress
                        }
                        placeholder="47 W 13th St, New York, NY 10011, USA"
                        readOnly
                      />
                      <FormFeedback>
                        {formik.errors.location?.fullAddress}
                      </FormFeedback>
                    </Col>

                    <Col md={12}>
                      {isLoaded ? (
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={center}
                          zoom={12}
                          options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                          }}
                          onClick={(e) => {
                            if (!e.latLng) return;
                            setField("location.latitude", e.latLng.lat());
                            setField("location.longitude", e.latLng.lng());
                          }}
                        >
                          <Marker
                            position={center}
                            draggable
                            onDragEnd={onMarkerDragEnd}
                            title="Drag to precise location"
                          />
                        </GoogleMap>
                      ) : (
                        <div className="border rounded p-4 text-muted">
                          Loading map…
                        </div>
                      )}
                    </Col>

                    <Col md={4}>
                      <Label>Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        name="location.latitude"
                        value={formik.values.location.latitude}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.location?.latitude &&
                          !!formik.errors.location?.latitude
                        }
                        placeholder="51.5199835418162"
                      />
                      <FormFeedback>
                        {formik.errors.location?.latitude}
                      </FormFeedback>
                    </Col>

                    <Col md={4}>
                      <Label>Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        name="location.longitude"
                        value={formik.values.location.longitude}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.location?.longitude &&
                          !!formik.errors.location?.longitude
                        }
                        placeholder="-0.03160791153367537"
                      />
                      <FormFeedback>
                        {formik.errors.location?.longitude}
                      </FormFeedback>
                    </Col>

                    <Col md={4} className="d-flex align-items-end">
                      <Button
                        color="secondary"
                        type="button"
                        onClick={useCurrentLocation}
                        className="w-100"
                      >
                        Use Current Location
                      </Button>
                    </Col>

                    <Col md={12}>
                      <Label>Timezone</Label>
                      <Select
                        options={[
                          {
                            value: "Europe/London",
                            label: "Europe/London (GMT)",
                          },
                          {
                            value: "Europe/Dublin",
                            label: "Europe/Dublin (GMT)",
                          },
                          {
                            value: "Europe/Paris",
                            label: "Europe/Paris (CET)",
                          },
                          {
                            value: "Europe/Berlin",
                            label: "Europe/Berlin (CET)",
                          },
                          {
                            value: "America/New_York",
                            label: "America/New_York (EST)",
                          },
                          {
                            value: "America/Los_Angeles",
                            label: "America/Los_Angeles (PST)",
                          },
                          { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
                          {
                            value: "Asia/Karachi",
                            label: "Asia/Karachi (PKT)",
                          },
                        ]}
                        value={{
                          value: formik.values.timezone,
                          label: formik.values.timezone,
                        }}
                        onChange={(opt) =>
                          setField("timezone", opt?.value || "Europe/London")
                        }
                        onBlur={() => formik.setFieldTouched("timezone", true)}
                        placeholder="Select timezone"
                        classNamePrefix="rs"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 99999,
                            backgroundColor: "#fff",
                          }),
                        }}
                      />
                      {formik.touched.timezone && formik.errors.timezone && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.timezone}
                        </div>
                      )}
                    </Col>
                    </>
                  ) : (
                    <>
                      <Col md={6}>
                        <Label className="text-muted small">Site name</Label>
                        <p className="mb-0 fw-medium">
                          {formik.values.location.siteName || "—"}
                        </p>
                      </Col>
                      <Col md={6}>
                        <Label className="text-muted small">Timezone</Label>
                        <p className="mb-0 fw-medium">
                          {formik.values.timezone || "—"}
                        </p>
                      </Col>
                      <Col md={12}>
                        <Label className="text-muted small">Full address</Label>
                        <p className="mb-0 fw-medium">
                          {formik.values.location.fullAddress || "—"}
                        </p>
                      </Col>
                      <Col md={12}>
                        {isLoaded ? (
                          <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={center}
                            zoom={12}
                            options={{
                              streetViewControl: false,
                              mapTypeControl: false,
                              draggable: false,
                              zoomControl: true,
                              scrollwheel: false,
                              disableDoubleClickZoom: true,
                            }}
                          >
                            <Marker position={center} draggable={false} />
                          </GoogleMap>
                        ) : (
                          <div className="border rounded p-4 text-muted">
                            Loading map…
                          </div>
                        )}
                      </Col>
                    </>
                  )}
                </Row>
              </div>

              {/* Notes Section */}
              <div className="section-white">
                <h5 className="section-header">Notes</h5>
                <Row className="g-3">
                  <Col md={12}>
                    <Label>Notes</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      name="notes"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.notes && !!formik.errors.notes}
                      placeholder="Please add any relevant notes for this booking."
                    />
                    <FormFeedback>{formik.errors.notes}</FormFeedback>
                  </Col>
                </Row>
              </div>

              {/* Navigation Buttons */}
              <div className="navigation-buttons">
                <Button
                  type="button"
                  color="secondary"
                  className="btn-next-full"
                  onClick={nextStep}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 2: Shifts (pending bookings only) ========== */}
          {step === 2 && canEditShifts && (
            <div className="custom-booking-card">
              {/* Custom Stepper */}
              <div className="custom-stepper">
                <div className="stepper-progress">
                  <div className={`stepper-progress-bar step-${step}`}></div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="mb-3 fw-bold text-dark">
                  Configure Your Shifts
                </h2>
                <p className="text-muted mb-0">
                  Set up your shift patterns, worker requirements, and
                  scheduling details. You can add up to 3 different shift
                  patterns for this booking.
                </p>
              </div>

              {/* Booking Type Section */}
              <div className="section-gray">
                <h5 className="section-header">Booking Type</h5>
                <p className="text-muted mb-3">
                  Select &quot;Urgent&quot; if the shift is scheduled to start
                  within the next 24 hours.
                </p>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="urgent"
                    name="isUrgent"
                    checked={formik.values.isUrgent}
                    onChange={(e) => {
                      formik.handleChange(e);

                      // When urgent booking is enabled, adjust end dates for existing patterns
                      if (e.target.checked) {
                        const copy = [...formik.values.shiftPattern];
                        copy.forEach((pattern, idx) => {
                          if (
                            pattern.startTime &&
                            pattern.endTime &&
                            pattern.startDate
                          ) {
                            const startTime = pattern.startTime;
                            const endTime = pattern.endTime;

                            // If end time is earlier than start time, it's an overnight shift
                            if (endTime < startTime) {
                              // For urgent bookings, end date should be next day for overnight shifts
                              const startDate = dayjs(pattern.startDate);
                              const nextDay = startDate
                                .add(1, "day")
                                .format("YYYY-MM-DD");
                              copy[idx].endDate = nextDay;
                            } else {
                              // Same day shift, end date should match start date
                              copy[idx].endDate = pattern.startDate;
                            }

                            // Update dates array
                            copy[idx].dates = updateDatesArray(idx, copy[idx]);
                          }
                        });
                        setField("shiftPattern", copy);
                      }

                      // Trigger validation for all shift patterns when urgent status changes
                      setTimeout(() => {
                        formik.values.shiftPattern.forEach((pattern, idx) => {
                          formik.validateField(`shiftPattern.${idx}.startDate`);
                          formik.validateField(`shiftPattern.${idx}.startTime`);
                          formik.validateField(`shiftPattern.${idx}.endDate`);
                        });
                      }, 100);
                    }}
                    className="form-check-input"
                  />
                  <Label for="urgent" className="form-check-label">
                    Urgent
                  </Label>
                </div>
              </div>

              {/* Shift Patterns */}
              {(formik.values.shiftPattern || []).map((p, idx) => {
                const pTouched = formik.touched.shiftPattern?.[idx] || {};
                const pErrors = formik.errors.shiftPattern?.[idx] || {};

                const roleValue =
                  roleOptions.find((o) => o.value === p.roleSlug) || null;

                return (
                  <Card
                    className="mb-3 section-dark-gray"
                    key={`pattern-${idx}`}
                  >
                    <CardHeader className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>Shift Pattern {idx + 1}</strong>
                        {formik.values.shiftPattern.length > 1 && (
                          <Badge color="light" className="ms-2">
                            {
                              generateDatesArray(
                                p.startDate,
                                p.endDate,
                                p.excludeWeekend,
                                p.excludeSpecificDates
                              ).length
                            }{" "}
                            date
                            {generateDatesArray(
                              p.startDate,
                              p.endDate,
                              p.excludeWeekend,
                              p.excludeSpecificDates
                            ).length !== 1
                              ? "s"
                              : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        {formik.values.shiftPattern.length > 1 && (
                          <Button
                            size="sm"
                            color="outline-danger"
                            onClick={() => removePattern(idx)}
                            className="btn-cross"
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Row className="g-3">
                        <Col md={6}>
                          <Label>Sector</Label>
                          <Select
                            isDisabled={sectorsLoading}
                            options={roleOptions.map((opt) => ({
                              value: opt.sectorSlug,
                              label: opt.group,
                            }))}
                            value={
                              roleOptions.find(
                                (opt) => opt.sectorSlug === p.sectorSlug
                              )
                                ? {
                                    value: p.sectorSlug,
                                    label: roleOptions.find(
                                      (opt) => opt.sectorSlug === p.sectorSlug
                                    )?.group,
                                  }
                                : null
                            }
                            onChange={(opt) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].sectorSlug = opt?.value || "";
                              copy[idx].roleSlug = ""; // Reset role when sector changes
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.sectorSlug`,
                                true
                              );
                            }}
                            placeholder={
                              sectorsLoading ? "Loading…" : "Select sector"
                            }
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                                backgroundColor: "#fff",
                              }),
                            }}
                          />
                          {pTouched?.sectorSlug && pErrors?.sectorSlug && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.sectorSlug}
                            </div>
                          )}
                        </Col>

                        <Col md={6}>
                          <Label>Role</Label>
                          <Select
                            isDisabled={sectorsLoading || !p.sectorSlug}
                            options={roleOptions.filter(
                              (opt) => opt.sectorSlug === p.sectorSlug
                            )}
                            value={roleValue}
                            onChange={(opt) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].roleSlug = opt?.value || "";
                              copy[idx].sectorSlug = opt?.sectorSlug || "";
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.roleSlug`,
                                true
                              );
                            }}
                            placeholder={
                              !p.sectorSlug
                                ? "Select sector first"
                                : sectorsLoading
                                ? "Loading…"
                                : "Select role"
                            }
                            classNamePrefix="rs"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                                backgroundColor: "#fff",
                              }),
                            }}
                          />
                          {pTouched?.roleSlug && pErrors?.roleSlug && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.roleSlug}
                            </div>
                          )}
                        </Col>

                        <Col md={12}>
                          <Label>PPE Provided?</Label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <Input
                                type="radio"
                                id={`ppe-yes-${idx}`}
                                name={`ppe-provided-${idx}`}
                                value="yes"
                                checked={p.hasPPEProvided === "yes"}
                                onClick={() => {
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].hasPPEProvided = "yes";
                                  setField("shiftPattern", copy);
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`ppe-yes-${idx}`}
                              >
                                Yes
                              </Label>
                            </div>
                            <div className="form-check">
                              <Input
                                type="radio"
                                id={`ppe-not-required-${idx}`}
                                name={`ppe-provided-${idx}`}
                                value="not_required"
                                checked={p.hasPPEProvided === "not_required"}
                                onClick={() => {
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].hasPPEProvided = "not_required";
                                  copy[idx].ppeInstruction = "";
                                  setField("shiftPattern", copy);
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`ppe-not-required-${idx}`}
                              >
                                Not Required
                              </Label>
                            </div>
                          </div>
                          {pTouched?.hasPPEProvided &&
                            pErrors?.hasPPEProvided && (
                              <div className="invalid-feedback d-block">
                                {pErrors?.hasPPEProvided}
                              </div>
                            )}
                        </Col>

                        {p.hasPPEProvided === "yes" && (
                          <Col md={12}>
                            <Label>PPE Details</Label>
                            <Input
                              name={`shiftPattern.${idx}.ppeInstruction`}
                              value={
                                formik.values.shiftPattern[idx]
                                  ?.ppeInstruction ?? ""
                              }
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `shiftPattern.${idx}.ppeInstruction`,
                                  e.target.value
                                )
                              }
                              onBlur={formik.handleBlur}
                              invalid={Boolean(
                                formik.touched.shiftPattern?.[idx]
                                  ?.ppeInstruction &&
                                  formik.errors.shiftPattern?.[idx]
                                    ?.ppeInstruction
                              )}
                              placeholder="Helmet, gloves"
                            />
                            {formik.touched.shiftPattern?.[idx]
                              ?.ppeInstruction &&
                              formik.errors.shiftPattern?.[idx]
                                ?.ppeInstruction && (
                                <div className="invalid-feedback d-block">
                                  {
                                    formik.errors.shiftPattern[idx]
                                      .ppeInstruction
                                  }
                                </div>
                              )}
                          </Col>
                        )}

                        <Col md={4}>
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={p.startDate}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].startDate = e.target.value;

                              // For urgent bookings, check if end date needs adjustment based on time fields
                              if (
                                formik.values.isUrgent &&
                                copy[idx].startTime &&
                                copy[idx].endTime
                              ) {
                                const startTime = copy[idx].startTime;
                                const endTime = copy[idx].endTime;

                                // If end time is earlier than start time, it's an overnight shift
                                if (endTime < startTime) {
                                  // For urgent bookings, end date should be next day for overnight shifts
                                  const startDate = dayjs(copy[idx].startDate);
                                  const nextDay = startDate
                                    .add(1, "day")
                                    .format("YYYY-MM-DD");
                                  copy[idx].endDate = nextDay;
                                } else {
                                  // Same day shift, end date should match start date
                                  copy[idx].endDate = copy[idx].startDate;
                                }
                              }

                              copy[idx].dates = updateDatesArray(
                                idx,
                                copy[idx]
                              );
                              setField("shiftPattern", copy);

                              // Trigger urgent validation
                              setTimeout(() => {
                                formik.validateField(
                                  `shiftPattern.${idx}.startDate`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.startTime`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.endDate`
                                );
                              }, 100);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.startDate`,
                                true
                              );
                            }}
                            className={
                              pTouched?.startDate && pErrors?.startDate
                                ? "is-invalid"
                                : ""
                            }
                            min={toISODate(dayjs())}
                          />
                          {pTouched?.startDate && pErrors?.startDate && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.startDate}
                            </div>
                          )}
                        </Col>

                        <Col md={4}>
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={p.endDate}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].endDate = e.target.value;
                              copy[idx].dates = updateDatesArray(
                                idx,
                                copy[idx]
                              );
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.endDate`,
                                true
                              );
                            }}
                            className={
                              pTouched?.endDate && pErrors?.endDate
                                ? "is-invalid"
                                : ""
                            }
                            min={p.startDate || toISODate(dayjs())}
                            disabled={formik.values.isUrgent}
                          />
                          {pTouched?.endDate && pErrors?.endDate && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.endDate}
                            </div>
                          )}
                        </Col>

                        <Col md={4} className="d-flex align-items-end">
                          {!formik.values.isUrgent && (
                            <div className="form-check">
                              <Input
                                type="checkbox"
                                id={`exclude-weekend-${idx}`}
                                checked={p.excludeWeekend}
                                onChange={(e) => {
                                  const copy = [...formik.values.shiftPattern];
                                  copy[idx].excludeWeekend = e.target.checked;
                                  copy[idx].dates = updateDatesArray(
                                    idx,
                                    copy[idx]
                                  );
                                  setField("shiftPattern", copy);
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`exclude-weekend-${idx}`}
                              >
                                Exclude Weekend
                              </Label>
                            </div>
                          )}
                        </Col>

                        {!formik.values.isUrgent && (
                          <Col md={12}>
                            <Label>Exclude specific dates (optional)</Label>
                            <ExcludeDatesPicker
                              startDate={p.startDate}
                              endDate={p.endDate}
                              excludeWeekend={p.excludeWeekend}
                              selectedDates={p.excludeSpecificDates}
                              onDatesChange={(dates) => {
                                const copy = [...formik.values.shiftPattern];
                                copy[idx].excludeSpecificDates = dates;
                                copy[idx].dates = updateDatesArray(
                                  idx,
                                  copy[idx]
                                );
                                setField("shiftPattern", copy);
                              }}
                            />
                          </Col>
                        )}

                        <Col md={3}>
                          <Label>Start Time</Label>
                          <Input
                            type="time"
                            value={p.startTime}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].startTime = e.target.value;

                              // Check for overnight shift and adjust end date if needed
                              if (
                                formik.values.isUrgent &&
                                copy[idx].endTime &&
                                e.target.value
                              ) {
                                const startTime = e.target.value;
                                const endTime = copy[idx].endTime;

                                // If end time is earlier than start time, it's an overnight shift
                                if (endTime < startTime) {
                                  // For urgent bookings, end date should be next day for overnight shifts
                                  const startDate = dayjs(copy[idx].startDate);
                                  const nextDay = startDate
                                    .add(1, "day")
                                    .format("YYYY-MM-DD");
                                  copy[idx].endDate = nextDay;
                                } else {
                                  // Same day shift, end date should match start date
                                  copy[idx].endDate = copy[idx].startDate;
                                }

                                // Update dates array
                                copy[idx].dates = updateDatesArray(
                                  idx,
                                  copy[idx]
                                );
                              }

                              // auto-adjust default break to rule if needed
                              const hours = diffHours(
                                copy[idx].startTime,
                                copy[idx].endTime
                              );
                              const minBreak = minBreakMinutesFor(hours);
                              if (
                                hhmmToMinutes(copy[idx].breakTime) < minBreak
                              ) {
                                copy[idx].breakTime = minutesToHHMM(minBreak);
                              }
                              setField("shiftPattern", copy);

                              // Trigger urgent validation
                              setTimeout(() => {
                                formik.validateField(
                                  `shiftPattern.${idx}.startDate`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.startTime`
                                );
                              }, 100);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.startTime`,
                                true
                              );
                            }}
                            className={
                              pTouched?.startTime && pErrors?.startTime
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {pTouched?.startTime && pErrors?.startTime && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.startTime}
                            </div>
                          )}
                        </Col>

                        <Col md={3}>
                          <Label>End Time</Label>
                          <Input
                            type="time"
                            value={p.endTime}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].endTime = e.target.value;

                              // Check for overnight shift and adjust end date if needed
                              if (
                                formik.values.isUrgent &&
                                copy[idx].startTime &&
                                e.target.value
                              ) {
                                const startTime = copy[idx].startTime;
                                const endTime = e.target.value;

                                // If end time is earlier than start time, it's an overnight shift
                                if (endTime < startTime) {
                                  // For urgent bookings, end date should be next day for overnight shifts
                                  const startDate = dayjs(copy[idx].startDate);
                                  const nextDay = startDate
                                    .add(1, "day")
                                    .format("YYYY-MM-DD");
                                  copy[idx].endDate = nextDay;
                                } else {
                                  // Same day shift, end date should match start date
                                  copy[idx].endDate = copy[idx].startDate;
                                }

                                // Update dates array
                                copy[idx].dates = updateDatesArray(
                                  idx,
                                  copy[idx]
                                );
                              }

                              const hours = diffHours(
                                copy[idx].startTime,
                                copy[idx].endTime
                              );
                              const minBreak = minBreakMinutesFor(hours);
                              if (
                                hhmmToMinutes(copy[idx].breakTime) < minBreak
                              ) {
                                copy[idx].breakTime = minutesToHHMM(minBreak);
                              }
                              setField("shiftPattern", copy);

                              // Trigger urgent validation
                              setTimeout(() => {
                                formik.validateField(
                                  `shiftPattern.${idx}.startDate`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.startTime`
                                );
                                formik.validateField(
                                  `shiftPattern.${idx}.endTime`
                                );
                              }, 100);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.endTime`,
                                true
                              );
                            }}
                            className={
                              pTouched?.endTime && pErrors?.endTime
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {pTouched?.endTime && pErrors?.endTime && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.endTime}
                            </div>
                          )}
                        </Col>

                        <Col md={3}>
                          <Label>Break Time (HH:MM)</Label>
                          <Input
                            type="text"
                            value={p.breakTime}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].breakTime = e.target.value;
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.breakTime`,
                                true
                              );
                            }}
                            placeholder="00:30"
                            className={
                              pTouched?.breakTime && pErrors?.breakTime
                                ? "is-invalid"
                                : ""
                            }
                          />
                          <small className="text-muted d-block">
                            Rule: &lt;6h → 0m; &gt;6h → ≥20m; &gt;9h → ≥30m
                          </small>
                          {pTouched?.breakTime && pErrors?.breakTime && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.breakTime}
                            </div>
                          )}
                        </Col>

                        <Col md={3}>
                          <Label>Number of workers</Label>
                          <Input
                            type="number"
                            min={1}
                            value={p.quantity}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].quantity = e.target.value;
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.quantity`,
                                true
                              );
                            }}
                            className={
                              pTouched?.quantity && pErrors?.quantity
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {pTouched?.quantity && pErrors?.quantity && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.quantity}
                            </div>
                          )}
                        </Col>

                        <Col md={12}>
                          <Label>Experience level</Label>
                          <Select
                            isMulti
                            options={experienceLevelOptions}
                            value={experienceLevelOptions.filter((opt) =>
                              (p.experienceLevel || []).includes(opt.value)
                            )}
                            onChange={(opts) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].experienceLevel = (opts || []).map(
                                (o) => o.value
                              );
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.experienceLevel`,
                                true
                              );
                            }}
                            placeholder="Select experience level"
                            menuPortalTarget={document.body}
                            classNamePrefix="rs"
                            styles={reactSelectMenuPortalStyles}
                          />
                        </Col>

                        <Col md={12}>
                          <Label>Certifications (optional)</Label>
                          <Select
                            isMulti
                            options={complianceCertOptions}
                            value={complianceCertOptions.filter((opt) =>
                              (p.certSlugs || []).includes(opt.value)
                            )}
                            onChange={(opts) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].certSlugs = (opts || []).map(
                                (o) => o.value
                              );
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.certSlugs`,
                                true
                              );
                            }}
                            placeholder="Select certifications"
                            isDisabled={complianceCertOptions.length === 0}
                            menuPortalTarget={document.body}
                            classNamePrefix="rs"
                            styles={reactSelectMenuPortalStyles}
                          />
                        </Col>

                        {/* Shift Supervisor Details */}
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <Label className="mb-0">
                              <strong>Shift Supervisor Details</strong>
                            </Label>
                            <Button
                              size="sm"
                              color="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addSupervisor(idx);
                              }}
                              type="button"
                            >
                              Add Supervisor
                            </Button>
                          </div>
                          {(p.supervisor || []).map((s, sIdx) => {
                            const sTouched = pTouched?.supervisor?.[sIdx] || {};
                            const sErrors = pErrors?.supervisor?.[sIdx] || {};
                            const isExisting = s.source === "existing";
                            const rowSupervisorOptions =
                              supervisorOptionsForShift(idx, sIdx);
                            const selectedSupervisor =
                              rowSupervisorOptions.find(
                                (o) => o.value === s.supervisorSlug
                              ) ||
                              supervisorOptions.find(
                                (o) => o.value === s.supervisorSlug
                              ) ||
                              null;
                            const duplicateInShift =
                              isDuplicateSupervisorInShift(
                                p.supervisor || [],
                                sIdx
                              );
                            return (
                              <div
                                key={sIdx}
                                className="mb-3 p-3 border rounded bg-white"
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-muted fw-semibold">
                                    Supervisor {sIdx + 1}
                                  </small>
                                  {p.supervisor.length > 1 && (
                                    <Button
                                      size="sm"
                                      color="outline-danger"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        removeSupervisor(idx, sIdx);
                                      }}
                                      type="button"
                                      className="btn-cross"
                                    >
                                      ×
                                    </Button>
                                  )}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                  <Button
                                    type="button"
                                    size="sm"
                                    color={
                                      isExisting
                                        ? "secondary"
                                        : "outline-secondary"
                                    }
                                    onClick={() =>
                                      setSupervisorSource(idx, sIdx, "existing")
                                    }
                                  >
                                    Select existing
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    color={
                                      !isExisting
                                        ? "secondary"
                                        : "outline-secondary"
                                    }
                                    onClick={() =>
                                      setSupervisorSource(idx, sIdx, "new")
                                    }
                                  >
                                    Add new
                                  </Button>
                                </div>
                                {isExisting ? (
                                  <Row className="g-2">
                                    <Col md={12}>
                                      <Label className="small mb-1">
                                        Supervisor
                                      </Label>
                                      <Select
                                        options={rowSupervisorOptions}
                                        value={selectedSupervisor}
                                        onChange={(opt) =>
                                          selectExistingSupervisor(
                                            idx,
                                            sIdx,
                                            opt
                                          )
                                        }
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.supervisorSlug`,
                                            true
                                          );
                                        }}
                                        isClearable
                                        isLoading={supervisorsLoading}
                                        placeholder={
                                          supervisorsLoading
                                            ? "Loading supervisors…"
                                            : rowSupervisorOptions.length
                                            ? "Select a supervisor"
                                            : "No other supervisors available"
                                        }
                                        styles={reactSelectMenuPortalStyles}
                                        className={
                                          (sTouched?.supervisorSlug &&
                                            sErrors?.supervisorSlug) ||
                                          duplicateInShift
                                            ? "is-invalid"
                                            : ""
                                        }
                                      />
                                      {sTouched?.supervisorSlug &&
                                        sErrors?.supervisorSlug && (
                                          <div className="invalid-feedback d-block">
                                            {sErrors.supervisorSlug}
                                          </div>
                                        )}
                                      {!sErrors?.supervisorSlug &&
                                        duplicateInShift && (
                                          <div className="invalid-feedback d-block">
                                            This supervisor is already added to
                                            this shift
                                          </div>
                                        )}
                                    </Col>
                                    {selectedSupervisor && (
                                      <Col md={12}>
                                        <div className="small text-muted">
                                          {s.name} · {s.email} · +
                                          {String(s.mobile || "").replace(
                                            /^\+/,
                                            ""
                                          )}
                                        </div>
                                      </Col>
                                    )}
                                  </Row>
                                ) : (
                                  <Row className="g-2 align-items-start">
                                    <Col md={4}>
                                      <Input
                                        placeholder="Name"
                                        value={s.name || ""}
                                        onChange={(e) => {
                                          const copy = [
                                            ...formik.values.shiftPattern,
                                          ];
                                          copy[idx].supervisor[sIdx].name =
                                            e.target.value;
                                          setField("shiftPattern", copy);
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.name`,
                                            true
                                          );
                                        }}
                                        className={
                                          sTouched?.name && sErrors?.name
                                            ? "is-invalid"
                                            : ""
                                        }
                                      />
                                      {sTouched?.name && sErrors?.name && (
                                        <div className="invalid-feedback d-block">
                                          {sErrors?.name}
                                        </div>
                                      )}
                                    </Col>
                                    <Col md={4}>
                                      <PhoneInput
                                        country="gb"
                                        enableSearch
                                        containerClass="w-100"
                                        inputProps={{
                                          name: `shiftPattern[${idx}].supervisor[${sIdx}].mobile`,
                                        }}
                                        inputClass={
                                          sTouched?.mobile && sErrors?.mobile
                                            ? "is-invalid"
                                            : ""
                                        }
                                        value={s.mobile || ""}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.mobile`,
                                            true
                                          );
                                        }}
                                        onChange={(v) => {
                                          const copy = [
                                            ...formik.values.shiftPattern,
                                          ];
                                          copy[idx].supervisor[sIdx].mobile = v;
                                          setField("shiftPattern", copy);
                                        }}
                                      />
                                      {sTouched?.mobile && sErrors?.mobile && (
                                        <div className="invalid-feedback d-block">
                                          {sErrors?.mobile}
                                        </div>
                                      )}
                                    </Col>
                                    <Col md={4}>
                                      <Input
                                        type="email"
                                        placeholder="Email (required)"
                                        value={s.email || ""}
                                        onChange={(e) => {
                                          const copy = [
                                            ...formik.values.shiftPattern,
                                          ];
                                          copy[idx].supervisor[sIdx].email =
                                            e.target.value;
                                          setField("shiftPattern", copy);
                                          setTimeout(() => {
                                            formik.validateField(
                                              `shiftPattern.${idx}.supervisor`
                                            );
                                          }, 0);
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `shiftPattern.${idx}.supervisor.${sIdx}.email`,
                                            true
                                          );
                                        }}
                                        className={
                                          sTouched?.email && sErrors?.email
                                            ? "is-invalid"
                                            : ""
                                        }
                                      />
                                      {sTouched?.email && sErrors?.email && (
                                        <div className="invalid-feedback d-block">
                                          {sErrors?.email}
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                )}
                              </div>
                            );
                          })}
                        </Col>

                        {/* Shift Details */}
                        <Col md={12}>
                          <Label>
                            <strong>Shift Details</strong>
                          </Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={p.shiftDetails}
                            onChange={(e) => {
                              const copy = [...formik.values.shiftPattern];
                              copy[idx].shiftDetails = e.target.value;
                              setField("shiftPattern", copy);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched(
                                `shiftPattern.${idx}.shiftDetails`,
                                true
                              );
                            }}
                            className={
                              pTouched?.shiftDetails && pErrors?.shiftDetails
                                ? "is-invalid"
                                : ""
                            }
                            placeholder="Main entrance, security required etc"
                          />
                          {pTouched?.shiftDetails && pErrors?.shiftDetails && (
                            <div className="invalid-feedback d-block">
                              {pErrors?.shiftDetails}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                );
              })}

              {/* Create Another Pattern Button */}
              {formik.values.shiftPattern.length <
                maxShiftPatternsForBooking(formik.values.isUrgent) && (
                <div className="mb-3">
                  <Button
                    color="warning"
                    size="lg"
                    onClick={addPattern}
                    className="w-100"
                  >
                    Create Another Pattern
                  </Button>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="navigation-buttons">
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    type="button"
                    onClick={prevStep}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#000000",
                      fontWeight: "bold",
                      padding: "0.5rem 0",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      fontSize: "1rem",
                    }}
                  >
                    ← Back
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    onClick={nextStep}
                    style={{
                      backgroundColor: "#2ca6a4",
                      borderColor: "#2ca6a4",
                      borderRadius: "8px",
                      padding: "0.5rem 1.5rem",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ========== Review & confirm ========== */}
          {isReviewStep && (
            <div className="custom-booking-card">
              {/* Custom Stepper */}
              <div className="custom-stepper">
                <div className="stepper-progress">
                  <div className={`stepper-progress-bar step-${step}`}></div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="mb-3 fw-bold text-dark">Review & Confirm</h2>
                <p className="text-muted mb-0">
                  Please review your booking details before saving your changes.
                </p>
              </div>

              {/* Booking Summary Card */}
              <Card className="mb-4">
                <CardHeader className="bg-primary">
                  <h6 className="mb-0 fw-semibold text-white">
                    Booking Summary
                  </h6>
                </CardHeader>
                <CardBody className="bg-light">
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <i className="bx bx-calendar me-2 text-primary"></i>
                        <div>
                          <small className="text-muted d-block">
                            Booking Type
                          </small>
                          <span className="fw-medium">
                            {formik.values.isUrgent ? "Urgent" : "Non Urgent"}
                          </span>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <i className="bx bx-map me-2 text-primary"></i>
                        <div>
                          <small className="text-muted d-block">Location</small>
                          <span className="fw-medium">
                            {formik.values.location.siteName || "—"}
                          </span>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <i className="bx bx-time me-2 text-primary"></i>
                        <div>
                          <small className="text-muted d-block">Timezone</small>
                          <span className="fw-medium">
                            {formik.values.timezone}
                          </span>
                        </div>
                      </div>
                    </Col>
                    {formik.values.hasPORequired && (
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <i className="bx bx-file me-2 text-primary"></i>
                          <div>
                            <small className="text-muted d-block">
                              PO Reference
                            </small>
                            <span className="fw-medium">
                              {formik.values.poReference || "—"}
                            </span>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                  {formik.values.location.fullAddress && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <small className="text-muted d-block">Full Address</small>
                      <span className="fw-medium">
                        {formik.values.location.fullAddress}
                      </span>
                    </div>
                  )}
                  {formik.values.notes && (
                    <div className="mt-3">
                      <small className="text-muted d-block">Notes</small>
                      <span className="fw-medium">{formik.values.notes}</span>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Shift Patterns */}
              {formik.values.shiftPattern.map((p, i) => {
                const generatedDates = generateDatesArray(
                  p.startDate,
                  p.endDate,
                  p.excludeWeekend,
                  p.excludeSpecificDates
                );

                return (
                  <Card key={i} className="mb-4">
                    <CardHeader className="bg-primary">
                      <h6 className="mb-0 fw-semibold text-white">
                        Shift Pattern {i + 1}
                      </h6>
                    </CardHeader>
                    <CardBody className="bg-light">
                      {/* Pattern Details */}
                      <div className="mb-4">
                        <div className="mb-2">
                          • <strong>Booking Location:</strong>{" "}
                          {formik.values.location.fullAddress || "—"}
                        </div>
                        {formik.values.hasPORequired && (
                          <div className="mb-2">
                            • <strong>PO Number:</strong>{" "}
                            {formik.values.poReference || "—"}
                          </div>
                        )}
                        <div className="mb-2">
                          • <strong>Sector:</strong> {p.sectorSlug || "—"}
                        </div>
                        <div className="mb-2">
                          • <strong>Role:</strong> {p.roleSlug || "—"}
                        </div>
                        <div className="mb-2">
                          • <strong>PPE Provided:</strong>{" "}
                          {p.hasPPEProvided === "yes"
                            ? p.ppeInstruction || "Yes"
                            : "Not Required"}
                        </div>
                        {(p.experienceLevel || []).length > 0 && (
                          <div className="mb-2">
                            • <strong>Experience level:</strong>{" "}
                            {(p.experienceLevel || []).join(", ")}
                          </div>
                        )}
                        {(p.certSlugs || []).length > 0 && (
                          <div className="mb-2">
                            • <strong>Certifications:</strong>{" "}
                            {(p.certSlugs || []).join(", ")}
                          </div>
                        )}
                        {p.supervisor.map((s, sIdx) => (
                          <div key={sIdx} className="mb-2">
                            • <strong>Supervisor {sIdx + 1}:</strong>{" "}
                            {s.source === "existing" && s.supervisorSlug
                              ? `${s.name || "Existing supervisor"} (saved)`
                              : `${s.name} | Phone: ${s.mobile}${
                                  s.email ? ` | Email: ${s.email}` : ""
                                }`}
                          </div>
                        ))}
                        <div className="mb-2">
                          • <strong>Shift Details:</strong>{" "}
                          {p.shiftDetails || "—"}
                        </div>
                        <div className="mb-2">
                          • <strong>Booking type:</strong>{" "}
                          {formik.values.isUrgent ? "Urgent" : "Non Urgent"}
                        </div>
                      </div>

                      {/* Dates Table */}
                      {generatedDates.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="custom-secondary-header">
                              <tr>
                                <th className="text-white fw-bold">Date</th>
                                <th className="text-white fw-bold">Time</th>
                                <th className="text-white fw-bold">Break</th>
                                <th className="text-white fw-bold">
                                  # of workers
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {generatedDates.map((date, dateIdx) => (
                                <tr key={dateIdx}>
                                  <td>{dayjs(date).format("DD-M-YYYY")}</td>
                                  <td>
                                    {dayjs(`2000-01-01 ${p.startTime}`).format(
                                      "hA"
                                    )}{" "}
                                    -{" "}
                                    {dayjs(`2000-01-01 ${p.endTime}`).format(
                                      "hA"
                                    )}
                                  </td>
                                  <td>
                                    {(() => {
                                      const breakMinutes = hhmmToMinutes(
                                        p.breakTime
                                      );
                                      return breakMinutes > 0
                                        ? `${breakMinutes} mints`
                                        : "0 mints";
                                    })()}
                                  </td>
                                  <td>{p.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                );
              })}

              {/* Navigation Buttons */}
              <div className="navigation-buttons">
                <Row className="g-3">
                  <Col md={6}>
                    <Button
                      type="button"
                      onClick={prevStep}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#000000",
                        fontWeight: "bold",
                        padding: "0.5rem 0",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "1rem",
                      }}
                    >
                      ← Back
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button
                      type="submit"
                      color="secondary"
                      className="btn-next-full"
                      disabled={isPending}
                    >
                      {isPending ? "Updating..." : "Update Booking"}
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Old navigation buttons removed - now using navigation inside cards */}
        </Form>
      </Container>

      {/* Success Modal */}
      <Modal isOpen={showSuccess} toggle={handleCloseModal} centered>
        <ModalHeader toggle={handleCloseModal}>Booking Updated</ModalHeader>
        <ModalBody>
          Your booking has been successfully updated. The changes have been
          saved and will be reflected in your bookings list.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCloseModal}>
            Go to Bookings
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BookingUpdate;
